import { useId, useMemo } from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/currency";

/**
 * Reusable tier picker for the booking flow. Renders as either a stack of
 * radio cards (great for ≤5 tiers) or a `<select>` dropdown (scales to 50+).
 *
 * `style="auto"` switches based on a 5-option threshold — radio if cardinality
 * is small, select otherwise. `radio` and `select` force the shape.
 */

export type TierOption = {
  id: string;
  name: string;
  category: string | null;
  unitPrice: number | null;
  currency: string | null;
};

export type TierPickerStyle = "auto" | "radio" | "select";

const AUTO_THRESHOLD = 5;

export function TierPicker({
  style,
  options,
  value,
  onChange,
}: {
  style: TierPickerStyle;
  options: TierOption[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  const resolved: "radio" | "select" = useMemo(() => {
    if (style === "radio") return "radio";
    if (style === "select") return "select";
    return options.length <= AUTO_THRESHOLD ? "radio" : "select";
  }, [style, options.length]);

  if (resolved === "radio") {
    return (
      <RadioCards options={options} value={value} onChange={onChange} />
    );
  }

  // Select mode — full-width native dropdown + a sub-line that previews the
  // chosen tier's price/category so the operator confirms the right pick.
  const picked = options.find((o) => o.id === value) ?? null;
  return (
    <div className="grid gap-2">
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger className="h-10">
          <SelectValue placeholder="Choose a tier…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.id} value={opt.id}>
              {opt.name}
              {opt.unitPrice != null
                ? ` — ${formatMoney(opt.currency, opt.unitPrice)}`
                : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {picked && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{picked.name}</p>
            {picked.category && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {picked.category}
              </p>
            )}
          </div>
          <span className="text-sm font-semibold tabular-nums shrink-0">
            {formatMoney(picked.currency, picked.unitPrice)}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Radix-backed radio card group. Each option is a card whose visual
 * "selected" state is driven by `peer-data-[state=checked]:*` styles
 * on the wrapping label. Arrow keys cycle between cards; Space selects.
 */
function RadioCards({
  options,
  value,
  onChange,
}: {
  options: TierOption[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  const groupId = useId();
  return (
    <RadioGroupPrimitive.Root
      value={value ?? undefined}
      onValueChange={onChange}
      className="grid gap-2"
    >
      {options.map((opt) => {
        const inputId = `${groupId}-${opt.id}`;
        return (
          <div key={opt.id} className="relative">
            <RadioGroupPrimitive.Item
              id={inputId}
              value={opt.id}
              className="peer sr-only"
            />
            <Label
              htmlFor={inputId}
              className={cn(
                "flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                "border-border hover:bg-muted/40",
                "peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary-light",
                "peer-focus-visible:ring-2 peer-focus-visible:ring-ring",
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className={cn(
                    "inline-block h-4 w-4 rounded-full border-2 shrink-0 transition-colors",
                    "border-muted-foreground/40",
                    "peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary",
                    value === opt.id && "border-primary bg-primary",
                  )}
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{opt.name}</p>
                  {opt.category && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {opt.category}
                    </p>
                  )}
                </div>
              </div>
              <span className="text-sm font-semibold tabular-nums shrink-0">
                {formatMoney(opt.currency, opt.unitPrice)}
              </span>
            </Label>
          </div>
        );
      })}
    </RadioGroupPrimitive.Root>
  );
}
